"use client"

import type { MapEdge, MapNode } from "@/types/map.types"

interface SpaceMapConnectionsProps {
  edges: MapEdge[]
  nodes: MapNode[]
  visitedNodeIds: string[]
  currentNodeId: string | null
  availableNodeIds: string[]
  height: number
  zoom?: number
}

export function SpaceMapConnections({
  edges,
  nodes,
  visitedNodeIds,
  currentNodeId,
  availableNodeIds,
  height,
  zoom = 1,
}: SpaceMapConnectionsProps) {
  // Create a map for quick node lookup
  const nodeMap = new Map(nodes.map(n => [n.id, n]))

  return (
    <svg
      className="absolute top-0 left-0 w-full pointer-events-none"
      style={{ height: height * zoom }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Glow filter for available paths */}
        <filter id="glow-cyan" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Glow filter for traveled paths */}
        <filter id="glow-gold" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {edges.map((edge) => {
        const fromNode = nodeMap.get(edge.fromNodeId)
        const toNode = nodeMap.get(edge.toNodeId)

        if (!fromNode || !toNode) return null

        // Determine edge state
        const isTraveled = visitedNodeIds.includes(edge.fromNodeId) &&
          (visitedNodeIds.includes(edge.toNodeId) || edge.toNodeId === currentNodeId)

        const isAvailablePath = (edge.fromNodeId === currentNodeId || visitedNodeIds.includes(edge.fromNodeId)) &&
          availableNodeIds.includes(edge.toNodeId)

        const isFromAvailable = availableNodeIds.includes(edge.fromNodeId)
        const isLocked = !isTraveled && !isAvailablePath && !isFromAvailable

        // Calculate line points (scaled by zoom)
        const x1 = fromNode.position.x * zoom
        const y1 = fromNode.position.y * zoom
        const x2 = toNode.position.x * zoom
        const y2 = toNode.position.y * zoom

        // Determine stroke color and style
        let strokeColor: string
        let strokeWidth: number
        let strokeDasharray: string
        let filter: string | undefined

        if (isTraveled) {
          strokeColor = "#f59e0b" // Amber/gold for traveled
          strokeWidth = 3 * zoom
          strokeDasharray = "none"
          filter = "url(#glow-gold)"
        } else if (isAvailablePath) {
          strokeColor = "#22d3ee" // Cyan for available
          strokeWidth = 2 * zoom
          strokeDasharray = `${8 * zoom} ${4 * zoom}`
          filter = "url(#glow-cyan)"
        } else if (isLocked) {
          strokeColor = "#374151" // Dark gray for locked
          strokeWidth = 1 * zoom
          strokeDasharray = `${4 * zoom} ${4 * zoom}`
          filter = undefined
        } else {
          strokeColor = "#6b7280" // Gray for neutral
          strokeWidth = 1.5 * zoom
          strokeDasharray = `${6 * zoom} ${3 * zoom}`
          filter = undefined
        }

        return (
          <g key={`${edge.fromNodeId}-${edge.toNodeId}`}>
            {/* Shadow/glow line */}
            {filter && (
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={strokeColor}
                strokeWidth={strokeWidth + 2 * zoom}
                strokeDasharray={strokeDasharray}
                strokeLinecap="round"
                opacity={0.5}
                filter={filter}
              />
            )}
            {/* Main line */}
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              strokeLinecap="round"
            />
          </g>
        )
      })}
    </svg>
  )
}
